/**
 * RoleHistoryTimeline component
 * Collapsible timeline showing role change audit history
 */
import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RoleBadge } from './RoleBadge'
import type { RoleChangeRecord } from '@/types/admin'
import type { RoleCode } from '../../types/admin'
import { cn } from '@/lib/utils'

export interface RoleHistoryTimelineProps {
  /** Array of role change records */
  history: RoleChangeRecord[]
  /** Whether the timeline is expanded by default */
  defaultExpanded?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Collapsible timeline component for displaying role change history
 */
export function RoleHistoryTimeline({
  history,
  defaultExpanded = false,
  className,
}: RoleHistoryTimelineProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    })
  }

  return (
    <div className={cn('border-t pt-4', className)}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium">Role History</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="text-xs"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Show
            </>
          )}
        </Button>
      </div>

      {expanded && (
        <div className="space-y-4 mt-4">
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No role changes recorded
            </p>
          ) : (
            history.map((record, index) => (
              <div
                key={`${record.changedAt}-${index}`}
                className="relative pl-6 pb-4 border-l-2 border-muted last:border-transparent"
              >
                {/* Timeline dot */}
                <div className="absolute -left-[5px] top-0 h-2 w-2 rounded-full bg-primary" />

                {/* Date */}
                <p className="text-xs text-muted-foreground mb-1">
                  {formatDate(record.changedAt)}
                </p>

                {/* Role transition */}
                <div className="flex items-center gap-2 mb-1">
                  {record.previousRole === null ? (
                    <span className="text-sm">
                      Initial role:{' '}
                      <RoleBadge role={record.newRole as RoleCode} size="sm" />
                    </span>
                  ) : (
                    <>
                      <RoleBadge role={record.previousRole as RoleCode} size="sm" />
                      <span className="text-muted-foreground">→</span>
                      <RoleBadge role={record.newRole as RoleCode} size="sm" />
                    </>
                  )}
                </div>

                {/* Changed by */}
                <p className="text-xs text-muted-foreground">
                  By: {record.changedByDisplayName}
                </p>

                {/* Reason */}
                {record.reason && (
                  <p className="text-xs text-muted-foreground italic mt-1">
                    "{record.reason}"
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
